import React from "react";
import { Box, Typography, Tooltip, Button } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { NAVY, GOLD, GOLD_SOFT, NAVY_DARK, BORDER, TEXT_SECONDARY } from "../../../theme/tokens";
import { LAYOUT_PRESETS, CATEGORY_LABELS, LayoutPreset, PresetCategory } from "./presets";
import MiniLayoutPreview from "./MiniLayoutPreview";

/**
 * "Layouts" tab of the designer palette. Renders visual preset cards
 * (a scaled mini-diagram of each layout's zones) grouped by category,
 * plus the rule-based Smart Layout entry point. Clicking a card asks the
 * page to apply it — the page handles the empty-canvas / replace-
 * confirmation / undo flow.
 */

interface LayoutPresetsPanelProps {
  orientation: "landscape" | "portrait";
  onApply: (preset: LayoutPreset) => void;
  onSmartLayout: () => void;
  onAiTemplate?: () => void;
}

const ORDER: PresetCategory[] = ["basic", "media", "restaurant", "vmpos", "information"];

// Feature flag (section 9). Off unless the build opts in; the backend endpoint
// is independently gated too, so both must be enabled for AI to run.
const AI_TEMPLATES_ENABLED = process.env.REACT_APP_FEATURE_AI_TEMPLATES === "true";

const LayoutPresetsPanel: React.FC<LayoutPresetsPanelProps> = ({ orientation, onApply, onSmartLayout, onAiTemplate }) => {
  const aspect = orientation === "portrait" ? "9 / 16" : "16 / 9";

  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: NAVY,
        textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
        Layouts
      </Typography>

      {/* Rule-based generator entry point. */}
      <Button
        fullWidth variant="contained" disableElevation size="small"
        startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
        onClick={onSmartLayout}
        sx={{
          mb: 1.5, textTransform: "none", fontWeight: 800, color: NAVY_DARK,
          background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
          "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
        }}
      >
        Smart Layout
      </Button>

      {AI_TEMPLATES_ENABLED && onAiTemplate && (
        <Button
          fullWidth variant="outlined" size="small"
          startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          onClick={onAiTemplate}
          sx={{ mb: 1.5, textTransform: "none", fontWeight: 800, borderColor: BORDER, color: NAVY }}
        >
          Create with AI
        </Button>
      )}

      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 1.5 }}>
        Click a layout to apply it. You can Undo afterwards.
      </Typography>

      {ORDER.map((cat) => {
        const items = LAYOUT_PRESETS.filter((p) => p.category === cat);
        if (items.length === 0) return null;
        return (
          <Box key={cat} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: TEXT_SECONDARY,
              textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
              {CATEGORY_LABELS[cat]}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              {items.map((preset) => (
                <Tooltip key={preset.id} title={preset.name} placement="top">
                  <Box
                    onClick={() => onApply(preset)}
                    sx={{
                      border: `1px solid ${BORDER}`, borderRadius: 1, p: 0.75,
                      cursor: "pointer", bgcolor: "#fff", transition: "all .12s",
                      "&:hover": { borderColor: GOLD, boxShadow: "0 2px 8px rgba(212,175,55,0.25)" },
                    }}
                  >
                    <MiniLayoutPreview zones={preset.zones} aspect={aspect} />
                    <Typography noWrap sx={{ fontSize: 10, color: NAVY, fontWeight: 600, mt: 0.5 }}>
                      {preset.name}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default LayoutPresetsPanel;
